import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useHttpClient } from "shared/hooks/http-hook";
import "./ArventoApiHealthCheck.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/health-check/arvento`;

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const localNow = () => toLocalInput(new Date());
const localTodayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toLocalInput(d);
};

const initParams = (entry) => {
  const p = {};
  (entry.params || []).forEach((pr) => {
    if (pr.type === "datetime") {
      p[pr.name] = pr.default === "now" ? localNow() : localTodayStart();
    } else {
      p[pr.name] = pr.default ?? "";
    }
  });
  return p;
};

const StatusPill = ({ state, durationMs }) => {
  const map = {
    idle: { label: "Denenmedi", cls: "idle" },
    running: { label: "Çalışıyor…", cls: "running" },
    ok: { label: "Başarılı", cls: "ok" },
    fail: { label: "Hata", cls: "fail" },
  };
  const s = map[state] || map.idle;
  return (
    <span className={`ahc-pill ahc-pill--${s.cls}`}>
      {s.label}
      {typeof durationMs === "number" && state !== "idle" && state !== "running" ? ` · ${durationMs} ms` : ""}
    </span>
  );
};

const ParamField = ({ param, value, onChange }) => {
  const common = {
    id: `p-${param.name}`,
    value: value ?? "",
    onChange: (e) => onChange(param.name, e.target.value),
  };
  return (
    <div className="ahc-field">
      <label htmlFor={common.id}>
        {param.name}
        {param.required ? <span className="ahc-req">*</span> : null}
      </label>
      {param.type === "select" ? (
        <select {...common}>
          {(param.options || []).map((o) => (
            <option key={o.v} value={o.v}>
              {o.l}
            </option>
          ))}
        </select>
      ) : param.type === "datetime" ? (
        <input type="datetime-local" {...common} />
      ) : (
        <input type={param.type === "number" ? "number" : "text"} placeholder={param.placeholder || ""} {...common} />
      )}
      {param.help ? <small className="ahc-help">{param.help}</small> : null}
    </div>
  );
};

const ResultView = ({ result }) => {
  const [showRaw, setShowRaw] = useState(false);
  const [rowFilter, setRowFilter] = useState("");
  if (!result) return null;

  const filteredRows =
    result.resultKind === "rows" && rowFilter.trim()
      ? result.sampleRows.filter((r) => JSON.stringify(r).toLowerCase().includes(rowFilter.trim().toLowerCase()))
      : result.sampleRows;

  if (!result.ok) {
    return (
      <div className="ahc-result ahc-result--fail">
        <div className="ahc-result__head">
          <strong>Hata</strong>
          <span className="ahc-tag">{result.errorType || "error"}</span>
          {result.arventoStatus ? <span className="ahc-tag">Arvento HTTP {result.arventoStatus}</span> : null}
          {typeof result.arventoCode === "number" ? <span className="ahc-tag">kod {result.arventoCode}</span> : null}
        </div>
        <div className="ahc-result__msg">{result.error}</div>
        {result.arventoRaw ? (
          <details className="ahc-details" open>
            <summary>Arvento ham yanıtı</summary>
            <pre>{result.arventoRaw}</pre>
          </details>
        ) : null}
        {result.sentParams && Object.keys(result.sentParams).length > 0 ? (
          <details className="ahc-details">
            <summary>Gönderilen parametreler</summary>
            <pre>{JSON.stringify(result.sentParams, null, 2)}</pre>
          </details>
        ) : null}
      </div>
    );
  }

  return (
    <div className="ahc-result ahc-result--ok">
      <div className="ahc-result__head">
        <strong>Başarılı</strong>
        {result.mutates ? <span className="ahc-tag ahc-tag--warn">yazma işlemi</span> : null}
        {result.resultKind === "rows" ? (
          <span className="ahc-tag">
            {result.rowCount} kayıt{result.truncated ? ` (ilk ${result.sampleRows.length} gösteriliyor)` : ""}
          </span>
        ) : (
          <span className="ahc-tag">{result.resultKind}</span>
        )}
      </div>

      {result.resultKind === "rows" ? (
        <details className="ahc-details" open={result.rowCount <= 50}>
          <summary>
            Kayıtlar ({result.rowCount}
            {result.truncated ? `, ilk ${result.sampleRows.length} gösteriliyor` : ""})
          </summary>
          <input
            className="ahc-row-filter"
            type="text"
            placeholder="Satır filtrele (ör. Cihaz No, alarm türü)…"
            value={rowFilter}
            onChange={(e) => setRowFilter(e.target.value)}
          />
          {rowFilter.trim() ? (
            <div className="ahc-row-filter-count">{filteredRows.length} eşleşme</div>
          ) : null}
          <pre>{JSON.stringify(filteredRows, null, 2)}</pre>
        </details>
      ) : null}

      {result.resultKind === "string" ? (
        <pre className="ahc-textout">
          {result.text}
          {result.truncated ? "\n… (kısaltıldı)" : ""}
        </pre>
      ) : null}

      {result.resultKind === "object" ? (
        result.raw ? (
          <>
            <button type="button" className="ahc-link-btn" onClick={() => setShowRaw((v) => !v)}>
              {showRaw ? "Ham yanıtı gizle" : "Ham yanıtı göster"}
            </button>
            {showRaw ? <pre>{JSON.stringify(result.raw, null, 2)}</pre> : null}
          </>
        ) : (
          <div className="ahc-result__msg">Yanıt alındı ancak çok büyük olduğu için gösterilmiyor.</div>
        )
      ) : null}

      {result.resultKind === "empty" ? <div className="ahc-result__msg">Boş yanıt (0 kayıt).</div> : null}
    </div>
  );
};

const EndpointCard = ({ entry, state, onChangeParam, onRun, onToggle }) => {
  const { status, result, expanded, params } = state;
  return (
    <div className={`ahc-card ahc-card--${status}`}>
      <div className="ahc-card__head" onClick={onToggle} role="button" tabIndex={0}>
        <div className="ahc-card__title">
          <span className="ahc-card__chevron">{expanded ? "▾" : "▸"}</span>
          <span className="ahc-method">{entry.method}</span>
          <span className="ahc-label">{entry.label}</span>
          {entry.authorizedHint ? <span className="ahc-tag ahc-tag--auth">kullanımda</span> : null}
          {entry.returnType ? <span className="ahc-tag">{entry.returnType}</span> : null}
          {entry.mutates ? <span className="ahc-tag ahc-tag--warn">yazma</span> : null}
        </div>
        <div className="ahc-card__actions">
          <StatusPill state={status} durationMs={result?.durationMs} />
          <button
            type="button"
            className={`ahc-run-btn${entry.mutates ? " ahc-run-btn--warn" : ""}`}
            disabled={status === "running"}
            onClick={(e) => {
              e.stopPropagation();
              onRun();
            }}
          >
            {status === "running" ? "…" : "Test et"}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="ahc-card__body">
          <p className="ahc-desc">{entry.description}</p>
          {entry.params && entry.params.length > 0 ? (
            <div className="ahc-fields">
              {entry.params.map((p) => (
                <ParamField key={p.name} param={p} value={params[p.name]} onChange={onChangeParam} />
              ))}
            </div>
          ) : (
            <p className="ahc-desc ahc-desc--muted">Bu metot ek parametre almaz (yalnızca kimlik bilgileri gönderilir).</p>
          )}
          <ResultView result={result} />
        </div>
      ) : null}
    </div>
  );
};

const ArventoApiHealthCheck = () => {
  const { sendRequest } = useHttpClient();
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [running, setRunning] = useState(false);
  // method -> { params, status, result, expanded }
  const [byMethod, setByMethod] = useState({});
  const cancelRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await sendRequest(`${API}/catalog`);
        if (!active) return;
        setCatalog(data.catalog || []);
        setConfig(data.config || null);
        const init = {};
        (data.catalog || []).forEach((e) => {
          init[e.method] = { params: initParams(e), status: "idle", result: null, expanded: false };
        });
        setByMethod(init);
      } catch (err) {
        if (active) setLoadError(err.message || "Katalog yüklenemedi.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [sendRequest]);

  const handleChangeParam = useCallback((method, name, value) => {
    setByMethod((prev) => ({
      ...prev,
      [method]: { ...prev[method], params: { ...prev[method].params, [name]: value } },
    }));
  }, []);

  const handleToggle = useCallback((method) => {
    setByMethod((prev) => ({ ...prev, [method]: { ...prev[method], expanded: !prev[method].expanded } }));
  }, []);

  const runMethod = useCallback(
    async (method) => {
      const cur = byMethod[method];
      if (!cur) return;
      setByMethod((prev) => ({ ...prev, [method]: { ...prev[method], status: "running" } }));
      try {
        const data = await sendRequest(
          `${API}/call`,
          "POST",
          JSON.stringify({ method, params: cur.params }),
        );
        setByMethod((prev) => ({
          ...prev,
          [method]: { ...prev[method], status: data.ok ? "ok" : "fail", result: data, expanded: true },
        }));
      } catch (err) {
        setByMethod((prev) => ({
          ...prev,
          [method]: {
            ...prev[method],
            status: "fail",
            result: { ok: false, errorType: "network", error: err.message || "İstek başarısız." },
            expanded: true,
          },
        }));
      }
    },
    [byMethod, sendRequest],
  );

  // Yazma işlemi (mutates) için tarayıcı onayı iste.
  const runMethodGuarded = useCallback(
    (entry) => {
      if (entry.mutates) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(`"${entry.method}" VERİ YAZAR (kalıcı değişiklik). Yine de çalıştırılsın mı?`);
        if (!ok) return;
      }
      runMethod(entry.method);
    },
    [runMethod],
  );

  const runAll = useCallback(async () => {
    setRunning(true);
    cancelRef.current = false;
    // Yalnızca okuma metotları; yazma metotları atlanır.
    const readOnly = catalog.filter((e) => !e.mutates);
    for (const e of readOnly) {
      if (cancelRef.current) break;
      // sıralı — Arvento her metot için 30 sn cooldown uygular (farklı metotlar birbirini beklemez)
      // eslint-disable-next-line no-await-in-loop
      await runMethod(e.method);
    }
    setRunning(false);
  }, [catalog, runMethod]);

  const stopAll = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (e) => e.method.toLowerCase().includes(q) || (e.label || "").toLowerCase().includes(q) || (e.group || "").toLowerCase().includes(q),
    );
  }, [catalog, search]);

  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach((e) => {
      if (!m.has(e.group)) m.set(e.group, []);
      m.get(e.group).push(e);
    });
    return [...m.entries()];
  }, [filtered]);

  const summary = useMemo(() => {
    let ok = 0;
    let fail = 0;
    let idle = 0;
    Object.values(byMethod).forEach((s) => {
      if (s.status === "ok") ok += 1;
      else if (s.status === "fail") fail += 1;
      else idle += 1;
    });
    return { ok, fail, idle, total: catalog.length };
  }, [byMethod, catalog.length]);

  if (loading) return <div className="ahc-loading">Katalog yükleniyor…</div>;
  if (loadError) return <div className="ahc-loading ahc-loading--err">{loadError}</div>;

  return (
    <div className="ahc">
      <div className="ahc-topbar">
        <div className="ahc-config">
          {config?.enabled ? (
            <span className="ahc-tag ahc-tag--ok">Arvento aktif</span>
          ) : (
            <span className="ahc-tag ahc-tag--warn">Arvento pasif</span>
          )}
          {config?.hasCredentials ? (
            <span className="ahc-tag">kullanıcı: {config.username || "—"}</span>
          ) : (
            <span className="ahc-tag ahc-tag--warn">kimlik eksik</span>
          )}
          <span className="ahc-tag">
            kota: {config?.dailyQuotaUsed ?? 0}/{config?.dailyQuota ?? 0}
          </span>
          <span className="ahc-summary">
            <b className="ahc-ok">{summary.ok}</b> başarılı · <b className="ahc-fail">{summary.fail}</b> hata ·{" "}
            <b>{summary.idle}</b> denenmedi · {summary.total} toplam
          </span>
        </div>
        <div className="ahc-controls">
          <input
            className="ahc-search"
            type="text"
            placeholder="Ara: metot / açıklama…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {running ? (
            <button type="button" className="ahc-run-btn ahc-run-btn--warn" onClick={stopAll}>
              Durdur
            </button>
          ) : (
            <button type="button" className="ahc-run-btn ahc-run-btn--primary" onClick={runAll}>
              Tümünü sırayla test et
            </button>
          )}
        </div>
      </div>

      {!config?.enabled ? (
        <div className="ahc-banner">
          Bu şirket için Arvento pasif veya kimlik bilgileri eksik. Testler <b>config</b> hatası döndürür. Ayarlar →
          Telemetri sekmesinden yapılandırabilirsiniz.
        </div>
      ) : null}

      {grouped.map(([group, entries]) => (
        <section key={group} className="ahc-group">
          <h3 className="ahc-group__title">
            {group} <span className="ahc-group__count">{entries.length}</span>
          </h3>
          <div className="ahc-group__list">
            {entries.map((entry) => (
              <EndpointCard
                key={entry.method}
                entry={entry}
                state={byMethod[entry.method] || { params: {}, status: "idle", result: null, expanded: false }}
                onChangeParam={(name, value) => handleChangeParam(entry.method, name, value)}
                onRun={() => runMethodGuarded(entry)}
                onToggle={() => handleToggle(entry.method)}
              />
            ))}
          </div>
        </section>
      ))}

      {grouped.length === 0 ? <div className="ahc-loading">Eşleşen metot yok.</div> : null}
    </div>
  );
};

export default ArventoApiHealthCheck;
